import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import Stack from '../../Stack';
import Text from '../../Text';
import Icon from '../../Icon';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import { colors } from '../../../../utils/theme';
import { getSpacing } from '../../../../utils/spacing';
import { containerLayout } from '../InputBase';

const ROW_HEIGHT = 44;
const LIST_MAX_HEIGHT = 220;

export type InlineListOption = { label: string; value: string };

export type InlineListFieldProps = {
    title: string;
    value: string | null;
    options: InlineListOption[];
    onChange: (value: string) => void;
    testID: string;
    error?: string;
    /** Option value to scroll near when nothing is selected yet. */
    fallbackScrollValue?: string;
};

/**
 * Select-look field (InputBase visual language) that expands inline to a scrolling
 * option list. Plain Views only — real Selects/popovers can't be used inside prompt
 * contents (the prompt portals to the app root, above
 * NestedScrollViewPortalHostContainer, where WebSelect's context assertion throws).
 */
const InlineListField: React.FC<InlineListFieldProps> = ({
    title,
    value,
    options,
    onChange,
    testID,
    error,
    fallbackScrollValue,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const scrollRef = React.useRef<ScrollView>(null);

    const selectedIndex = value ? options.findIndex((option) => option.value === value) : -1;
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : null;

    const scrollToSelected = () => {
        const targetIndex =
            selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => o.value === fallbackScrollValue);
        scrollRef.current?.scrollTo({ y: Math.max(0, (targetIndex - 1) * ROW_HEIGHT), animated: false });
    };

    return (
        <Stack vertical space={getSpacing(1)} align="flex-start" fullWidth>
            <PressableDynamicOpacity
                onPress={() => setIsExpanded((prev) => !prev)}
                testID={testID}
                style={{ width: '100%' }}
            >
                <View
                    style={{
                        minHeight: containerLayout.minHeight,
                        width: '100%',
                        backgroundColor: colors['Grey-Light2'],
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5,
                        borderBottomWidth: 1,
                        borderBottomColor: !!error ? colors.Alert : colors.Dividers,
                        paddingLeft: containerLayout.leftPadding,
                        paddingRight: containerLayout.rightPadding,
                        paddingVertical: containerLayout.topPadding,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <View style={{ flex: 1, justifyContent: 'center' }}>
                        {selectedLabel ? (
                            <Stack vertical space={getSpacing(0.5)}>
                                <Text variant="input-label">{title}</Text>
                                <Text variant="input-text">{selectedLabel}</Text>
                            </Stack>
                        ) : (
                            <Text variant="input-text">{title}</Text>
                        )}
                    </View>
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size="checkbox-icon" />
                </View>
            </PressableDynamicOpacity>
            {isExpanded ? (
                <View
                    style={{
                        width: '100%',
                        maxHeight: LIST_MAX_HEIGHT,
                        backgroundColor: colors.White,
                        borderWidth: 1,
                        borderColor: colors.Dividers,
                        borderBottomLeftRadius: 5,
                        borderBottomRightRadius: 5,
                    }}
                >
                    <ScrollView ref={scrollRef} onLayout={scrollToSelected} nestedScrollEnabled>
                        {options.map((option) => {
                            const isSelected = option.value === value;
                            return (
                                <PressableDynamicOpacity
                                    key={option.value}
                                    testID={`${testID}.option-${option.value}`}
                                    onPress={() => {
                                        onChange(option.value);
                                        setIsExpanded(false);
                                    }}
                                >
                                    <View
                                        style={{
                                            height: ROW_HEIGHT,
                                            justifyContent: 'center',
                                            paddingHorizontal: containerLayout.leftPadding,
                                            backgroundColor: isSelected ? colors.Primary : undefined,
                                        }}
                                    >
                                        <Text variant="body-1" color={isSelected ? 'White' : 'Dark-Text'}>
                                            {option.label}
                                        </Text>
                                    </View>
                                </PressableDynamicOpacity>
                            );
                        })}
                    </ScrollView>
                </View>
            ) : null}
            {error ? <Text variant="input-error">{error}</Text> : null}
        </Stack>
    );
};

export default React.memo(InlineListField);
